import { apiGet, apiPost, apiPut } from '../../../adapters/api-client/api';
import { ReviewReasonCode } from '../domain/review-reasons';

export type TeacherApplicationState =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'UNDER_REVIEW'
  | 'APPROVED'
  | 'REJECTED'
  | 'NEEDS_RESUBMIT';

export type TeacherDocumentType = 'ID' | 'CREDENTIAL' | 'REFERENCE';
/**
 * AWAITING_UPLOAD is a key that was reserved and never filled: the upload URL
 * was issued, and the browser's PUT to it did not complete. It does not count
 * towards the required documents (S3.1).
 */
export type TeacherDocumentStatus = 'AWAITING_UPLOAD' | 'PENDING' | 'ACCEPTED' | 'REJECTED';

/**
 * The malware scanner's verdict on the stored file (GuardDuty Malware
 * Protection for S3; CTO decision 12 Oct 2026). Mirrors the backend's
 * kernel/storage/malware-scan-status.ts. Only CLEAN can be opened (AD-A07-08).
 */
export type MalwareScanStatus =
  | 'PENDING'
  | 'CLEAN'
  | 'THREATS_FOUND'
  | 'UNSUPPORTED'
  | 'ACCESS_DENIED'
  | 'FAILED';

export type EmailCommunicationPreference = 'ALL' | 'ESSENTIAL_ONLY';

export interface TeacherApplicationProfile {
  languages: string[];
  yearsExperience: number;
  certifications: string[];
  methodology: string;
  subjectCoverage: string[];
  levelCoverage: string[];
  headlineHourlyRate: number;
  bio?: string;

  /**
   * Identity, contact and preferences (TCH-03-FR-02). Optional in the type
   * because a draft is saveable half-finished; the server requires the identity
   * and contact ones at submission.
   */
  legalName?: string;
  phoneCountryCode?: string;
  phoneNumber?: string;
  country?: string;
  timezone?: string;
  ageRangesServed?: string[];
  emailCommunicationPreference?: EmailCommunicationPreference;
  marketingOptIn?: boolean;
  referralSource?: string;
}

export interface TeacherApplication {
  applicationId: string;
  teacherId: string;
  state: TeacherApplicationState;
  profile: TeacherApplicationProfile;
  submittedAt?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  reviewReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TeacherDocument {
  documentId: string;
  applicationId: string;
  teacherId: string;
  type: TeacherDocumentType;
  status: TeacherDocumentStatus;
  s3Key: string;
  contentType: string;
  fileName?: string;
  rejectionReason?: string;
  /** Absent on documents uploaded before scanning existed, and while AWAITING_UPLOAD. */
  scanStatus?: MalwareScanStatus;
  uploadedAt: string;
  reviewedAt?: string;
  updatedAt: string;
}

export interface ApplicationDetail {
  application: TeacherApplication;
  documents: TeacherDocument[];
  missingRequiredDocuments: string[];
}

const BASE = '/api/v1/teacher-applications';

export function getMyApplication(): Promise<ApplicationDetail> {
  return apiGet(`${BASE}/me`);
}

export function upsertMyApplication(
  profile: TeacherApplicationProfile,
): Promise<{ application: TeacherApplication }> {
  return apiPut(`${BASE}/me`, { profile });
}

export function submitMyApplication(): Promise<{ application: TeacherApplication }> {
  return apiPost(`${BASE}/me/submit`, {});
}

export function presignDocument(
  applicationId: string,
  body: { documentType: TeacherDocumentType; contentType: string; fileName?: string },
): Promise<PresignedDocumentUpload> {
  return apiPost(`${BASE}/${applicationId}/documents/presign`, body);
}

/**
 * Where and how to send the file.
 *
 * `POST` is a presigned S3 POST policy: send `uploadFields` as multipart form
 * fields, then the file as `file`, last. S3 checks the size against the
 * policy and refuses anything over the limit (TCH-04-FR-03) — a presigned PUT
 * cannot. `PUT` is the local stub.
 */
export interface PresignedDocumentUpload {
  documentId: string;
  uploadUrl: string;
  uploadMethod?: 'POST' | 'PUT';
  uploadFields?: Record<string, string>;
  s3Key: string;
  expiresAt: string;
}

export function confirmDocument(
  applicationId: string,
  body: { documentId: string; s3Key: string },
): Promise<{ document: TeacherDocument }> {
  return apiPost(`${BASE}/${applicationId}/documents/confirm`, body);
}

// Admin endpoints
const ADMIN_BASE = '/api/v1/admin/teacher-applications';

export function listAdminQueue(state?: TeacherApplicationState): Promise<{
  applications: TeacherApplication[];
}> {
  const path = state ? `${ADMIN_BASE}?state=${state}` : ADMIN_BASE;
  return apiGet(path);
}

export function getAdminApplicationDetail(applicationId: string): Promise<{
  application: TeacherApplication;
  documents: TeacherDocument[];
}> {
  return apiGet(`${ADMIN_BASE}/${applicationId}`);
}

export function startAdminReview(
  applicationId: string,
): Promise<{ application: TeacherApplication }> {
  return apiPost(`${ADMIN_BASE}/${applicationId}/start-review`, {});
}

export function approveApplication(
  applicationId: string,
): Promise<{ application: TeacherApplication }> {
  return apiPost(`${ADMIN_BASE}/${applicationId}/approve`, {});
}

/**
 * @param reasonCode which safe reason the teacher is shown.
 * @param reason the reviewer's own note. Kept on the record for audit and
 * support; the API never returns it to the teacher.
 */
export function rejectApplication(
  applicationId: string,
  reasonCode: ReviewReasonCode,
  reason: string,
): Promise<{ application: TeacherApplication }> {
  return apiPost(`${ADMIN_BASE}/${applicationId}/reject`, { reasonCode, reason });
}

export function requestResubmit(
  applicationId: string,
  body: {
    /** Which safe reason the teacher is shown. */
    reasonCode: ReviewReasonCode;
    /** The reviewer's own note. Internal. */
    reason: string;
    rejectedDocumentIds: string[];
    documentRejectionReasons?: Record<string, string>;
    documentReasonCodes?: Record<string, ReviewReasonCode>;
  },
): Promise<{ application: TeacherApplication }> {
  return apiPost(`${ADMIN_BASE}/${applicationId}/request-resubmit`, body);
}

/**
 * A short-lived, audited link to one applicant document (AD-A07-08,
 * ADM-07-FR-03). Issued only for a file scanned clean, only while the
 * application is under review, and only to an operator holding the authority;
 * every issue is written to the audit log. Ask for a new one each time — it
 * expires in two minutes.
 */
export function getAdminDocumentLink(
  applicationId: string,
  teacherId: string,
  documentId: string,
): Promise<{ downloadUrl: string; expiresAt: string }> {
  return apiGet(`${ADMIN_BASE}/${applicationId}/documents/${teacherId}/${documentId}/download-url`);
}
